import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { ResponsivePie } from '@nivo/pie';

import { getTranslation } from 'helpers/getTranslation';

import { Button } from 'components/shared/Button';
import { Typography } from 'components/shared/Typography';
import { CuratorReportLoading } from '../SchoolCuratorReportPage/components/CuratorReportLoading';
import { Avatar } from 'components/shared/Avatar';
import { CompletedIcon } from 'components/shared/CompletedIcon';
import { Icon } from 'components/shared/Icon';
import { Collapsible } from 'components/shared/Collapsible';
import { MenuLayout } from 'components/Layout';
import { EmptyState } from 'components/EmptyState';
import { CoursePreview } from 'components/CoursePreview';

import {
  formatPromocodeAccessRange,
  formatPromocodeCode,
  formatProgressPercent,
  formatStudentRegisteredAt,
  getMetricCards,
  getRiskBadgeClassName,
  getRiskLevelLabel,
  getRiskNotApplicableBadgeClassName,
  getRiskNotApplicableLabel,
  getStatusBadgeClassName,
  isRiskApplicable,
  getActiveCourseBreakdown,
  getCurrentLessonIdForBreakdown,
  getCourseProgressDonutData,
  getCourseProgressDonutPadAngle,
  getCourseTabLabel,
  formatLearnTimeSeconds,
  getStudentAvatarText,
  getStudentDisplayName,
  getStudentStatusLabel,
  hasStudentDetailName,
  OVERVIEW_COURSES_PROGRESS_COLORS,
  type CuratorStudentDetailCourseBreakdown,
  type CuratorStudentDetailData,
  type CuratorStudentDetailLesson,
  type CuratorStudentDetailModule,
  type CuratorStudentDetailOverviewCourse,
} from './SchoolCuratorStudentPage.helpers';

import styles from './SchoolCuratorStudentPage.module.scss';

const LessonStatusIcon = ({
  completed,
  isCurrent,
}: {
  completed: boolean;
  isCurrent?: boolean;
}) => {
  if (completed) {
    return <CompletedIcon className={styles.lessonStatusIconCompleted} />;
  }

  if (isCurrent) {
    return (
      <span className={cn(styles.lessonIcon, styles.lessonIconCurrent)}>
        <span className={styles.lessonIconDot} />
      </span>
    );
  }

  return <span className={cn(styles.lessonIcon, styles.lessonIconPending)} />;
};

const LessonRow = ({
  lesson,
  isCurrent,
}: {
  lesson: CuratorStudentDetailLesson;
  isCurrent?: boolean;
}) => (
  <div className={styles.lessonItem}>
    <LessonStatusIcon completed={lesson.completed} isCurrent={isCurrent} />
    <Typography
      className={cn(styles.lessonTitle, {
        [styles.lessonTitleCompleted]: lesson.completed,
      })}
      variant="body3"
      weight="medium"
      mode="compact"
    >
      {lesson.title || '—'}
    </Typography>
    {isCurrent ? (
      <div className={cn(styles.badge, styles.lessonCurrentBadge)}>
        <Typography
          className={styles.badgeText}
          variant="caption"
          weight="medium"
          mode="compact"
        >
          {getTranslation('school_curator_student_page.current_lesson_tag')}
        </Typography>
      </div>
    ) : null}
  </div>
);

const OverviewPieTooltip = ({ text }: { text: string }) => (
  <div className={styles.chartTooltip}>
    <Typography
      className={styles.chartTooltipText}
      variant="caption"
      mode="compact"
    >
      {text}
    </Typography>
  </div>
);

const DonutCenteredMetric = ({
  progress,
  centerX,
  centerY,
}: {
  progress: number;
  centerX: number;
  centerY: number;
}) => (
  <text
    x={centerX}
    y={centerY}
    textAnchor="middle"
    dominantBaseline="central"
    className={styles.donutCenterMetric}
  >
    {formatProgressPercent(progress)}
  </text>
);

const CompactCourseDonut = ({
  progress,
  courseColor,
  className,
}: {
  progress: number;
  courseColor: string;
  className?: string;
}) => (
  <div className={cn(styles.compactDonut, className)}>
    <ResponsivePie
      colors={{ datum: 'data.color' }}
      data={getCourseProgressDonutData(progress, courseColor)}
      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
      innerRadius={0.72}
      padAngle={getCourseProgressDonutPadAngle(progress)}
      cornerRadius={2}
      activeOuterRadiusOffset={4}
      enableArcLabels={false}
      enableArcLinkLabels={false}
      isInteractive={false}
      layers={[
        'arcs',
        (props: { centerX: number; centerY: number }) => (
          <text
            x={props.centerX}
            y={props.centerY}
            textAnchor="middle"
            dominantBaseline="central"
            className={styles.compactDonutCenterMetric}
          >
            {formatProgressPercent(progress)}
          </text>
        ),
      ]}
    />
  </div>
);

const BreakdownCourseTab = ({
  breakdown,
  progress,
  courseColor,
  isActive,
  onClick,
}: {
  breakdown: CuratorStudentDetailCourseBreakdown;
  progress: number;
  courseColor: string;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    role="tab"
    aria-selected={isActive}
    className={cn(styles.breakdownCourseTab, {
      [styles.breakdownCourseTabActive]: isActive,
    })}
    onClick={onClick}
  >
    <CoursePreview
      className={styles.breakdownCourseTabPreview}
      preview={breakdown.preview}
    />
    <CompactCourseDonut progress={progress} courseColor={courseColor} />
    <Typography
      className={styles.breakdownCourseTabTitle}
      variant="body2"
      weight="medium"
      mode="compact"
      component="span"
    >
      {getCourseTabLabel(breakdown.title)}
    </Typography>
  </button>
);

const CourseProgressDonutCard = ({
  course,
  courseColor,
  buildCourseLink,
  locale,
}: {
  course: CuratorStudentDetailOverviewCourse;
  courseColor: string;
  buildCourseLink: (courseId: string) => string;
  locale: string;
}) => {
  const donutData = getCourseProgressDonutData(course.progress, courseColor);

  const cardContent = (
    <>
      <div className={styles.overviewCourseHeader}>
        <Typography
          className={styles.overviewCourseTitle}
          variant="body2"
          weight="medium"
          mode="compact"
        >
          {course.title}
        </Typography>
        {course.isCurrent ? (
          <div className={cn(styles.badge, styles.overviewCurrentBadge)}>
            <Typography
              className={styles.badgeText}
              variant="caption"
              weight="medium"
              mode="compact"
            >
              {getTranslation(
                'school_curator_student_page.current_course_badge',
              )}
            </Typography>
          </div>
        ) : null}
      </div>

      <div className={styles.courseDonutInner}>
        <ResponsivePie
          colors={{ datum: 'data.color' }}
          data={donutData}
          margin={{ top: 8, right: 0, bottom: 8, left: 0 }}
          innerRadius={0.72}
          padAngle={getCourseProgressDonutPadAngle(course.progress)}
          cornerRadius={3}
          activeOuterRadiusOffset={6}
          enableArcLabels={false}
          enableArcLinkLabels={false}
          layers={[
            'arcs',
            (props: { centerX: number; centerY: number }) => (
              <DonutCenteredMetric
                centerX={props.centerX}
                centerY={props.centerY}
                progress={course.progress}
              />
            ),
          ]}
          tooltip={() => (
            <OverviewPieTooltip
              text={getTranslation(
                'school_curator_student_page.chart_tooltip_progress',
                {
                  title: course.title,
                  progress: course.progress,
                  completed: course.completedLessonsCount,
                  total: course.totalLessonsCount,
                },
              )}
            />
          )}
        />
      </div>

      <Typography
        className={styles.overviewCourseLessons}
        variant="caption"
        mode="compact"
      >
        {getTranslation('school_curator_student_page.lessons_completed', {
          completed: course.completedLessonsCount,
          total: course.totalLessonsCount,
        })}
      </Typography>

      <Typography
        className={styles.overviewCourseLessons}
        variant="caption"
        mode="compact"
      >
        {getTranslation('school_curator_student_page.learn_time_spent', {
          time: formatLearnTimeSeconds(course.learnTimeSeconds, locale),
        })}
      </Typography>
    </>
  );

  if (course.isCurrent) {
    return (
      <div
        className={cn(styles.overviewCourseCard, styles.overviewCourseCardCurrent)}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <Link
      className={styles.overviewCourseCard}
      to={buildCourseLink(course.id)}
    >
      {cardContent}
    </Link>
  );
};

const ModuleAccordion = ({
  module,
  currentLessonId,
}: {
  module: CuratorStudentDetailModule;
  currentLessonId: string | null;
}) => (
  <Collapsible
    className={styles.moduleCard}
    openedClassName={cn(styles.moduleCard, styles.moduleCardOpened)}
    overflowWhenOpen="visible"
    transitionTime={150}
    triggerTagName="button"
    trigger={
      <div className={styles.moduleHeader}>
        <div className={styles.moduleTitleWrap}>
          <Typography
            className={styles.moduleTitle}
            variant="body2"
            weight="medium"
            mode="compact"
          >
            {module.title}
          </Typography>
          <Typography
            className={styles.moduleProgress}
            variant="caption"
            mode="compact"
          >
            {getTranslation('school_curator_student_page.module_progress', {
              progress: formatProgressPercent(module.progress),
              completed: module.completed
                ? getTranslation('school_curator_student_page.module_completed')
                : getTranslation(
                    'school_curator_student_page.module_in_progress',
                  ),
            })}
          </Typography>
        </div>
        <Icon className={styles.moduleChevron} name="chevronDown" />
      </div>
    }
  >
    <div className={styles.lessonList}>
      {module.lessons.map((lesson) => (
        <LessonRow
          key={lesson.id}
          lesson={lesson}
          isCurrent={lesson.id === currentLessonId}
        />
      ))}
    </div>
  </Collapsible>
);

const CourseBreakdownContent = ({
  breakdown,
  currentLessonId,
}: {
  breakdown: CuratorStudentDetailCourseBreakdown;
  currentLessonId: string | null;
}) => {
  const hasModules =
    breakdown.structure === 'MODULES' && Boolean(breakdown.modules?.length);
  const hasLessons =
    breakdown.structure === 'LESSONS' && Boolean(breakdown.lessons?.length);

  if (!hasModules && !hasLessons) {
    return (
      <Typography variant="body3" mode="compact">
        {getTranslation('school_curator_student_page.no_lessons')}
      </Typography>
    );
  }

  return (
    <>
      {hasModules ? (
        <div className={styles.moduleList}>
          {breakdown.modules?.map((module) => (
            <ModuleAccordion
              key={module.id}
              module={module}
              currentLessonId={currentLessonId}
            />
          ))}
        </div>
      ) : null}

      {hasLessons ? (
        <div className={styles.lessonList}>
          {breakdown.lessons?.map((lesson) => (
            <LessonRow
              key={lesson.id}
              lesson={lesson}
              isCurrent={lesson.id === currentLessonId}
            />
          ))}
        </div>
      ) : null}
    </>
  );
};

const ProgressBreakdownSection = ({
  detailData,
}: {
  detailData: CuratorStudentDetailData;
}) => {
  const progressByCourseId = new Map(
    detailData.overview.courses.map((course) => [course.id, course.progress]),
  );

  const breakdowns =
    detailData.courseBreakdowns.length > 0
      ? detailData.courseBreakdowns
      : [
          {
            id: detailData.course.id,
            title: detailData.course.title,
            structure: detailData.course.structure,
            preview: null,
            progress:
              progressByCourseId.get(detailData.course.id) ??
              detailData.summary.progress ??
              0,
            modules: detailData.modules,
            lessons: detailData.lessons,
          },
        ];

  const [activeBreakdownCourseId, setActiveBreakdownCourseId] = useState(
    detailData.course.id,
  );

  useEffect(() => {
    setActiveBreakdownCourseId(detailData.course.id);
  }, [detailData.course.id]);

  const activeBreakdown = getActiveCourseBreakdown(
    breakdowns,
    activeBreakdownCourseId,
  );

  const currentLessonId = activeBreakdown
    ? getCurrentLessonIdForBreakdown(
        activeBreakdown,
        detailData.course.id,
        detailData.summary.currentLesson?.id,
      )
    : null;

  return (
    <div className={styles.section}>
      <Typography
        className={styles.sectionTitle}
        variant="body1"
        weight="medium"
        mode="compact"
      >
        {getTranslation('school_curator_student_page.progress_breakdown')}
      </Typography>

      {breakdowns.length >= 1 ? (
        <div className={styles.breakdownCourseTabs} role="tablist">
          {breakdowns.map((breakdown, index) => {
            const progress =
              breakdown.progress ??
              progressByCourseId.get(breakdown.id) ??
              0;
            const courseColor =
              OVERVIEW_COURSES_PROGRESS_COLORS[
                index % OVERVIEW_COURSES_PROGRESS_COLORS.length
              ];

            return (
              <BreakdownCourseTab
                key={breakdown.id}
                breakdown={breakdown}
                progress={progress}
                courseColor={courseColor}
                isActive={activeBreakdownCourseId === breakdown.id}
                onClick={() => setActiveBreakdownCourseId(breakdown.id)}
              />
            );
          })}
        </div>
      ) : null}

      {activeBreakdown ? (
        <CourseBreakdownContent
          breakdown={activeBreakdown}
          currentLessonId={currentLessonId}
        />
      ) : (
        <Typography variant="body3" mode="compact">
          {getTranslation('school_curator_student_page.no_lessons')}
        </Typography>
      )}
    </div>
  );
};

const SchoolCuratorStudentPage = ({
  locale,
  isPublic,
  loading,
  detailData,
  showBackButton,
  onBackClick,
  onCopyPromocodeClick,
  buildCourseLink,
  missingCourse,
}: {
  locale: string;
  isPublic: boolean;
  loading: boolean;
  detailData: CuratorStudentDetailData | null;
  showBackButton: boolean;
  onBackClick: () => void;
  onCopyPromocodeClick: () => void;
  buildCourseLink: (courseId: string) => string;
  missingCourse: boolean;
}) => {
  const pageContent = (
    <div
      className={cn(styles.page, {
        [styles.pagePublic]: isPublic,
      })}
    >
      <div
        className={cn(styles.content, {
          [styles.contentProtected]: !isPublic,
        })}
      >
        {showBackButton ? (
          <Button
            className={styles.backButton}
            variant="outlined"
            size="md"
            startIcon={<Icon name="chevronLeft" />}
            onClick={onBackClick}
          >
            {getTranslation('school_curator_student_page.back_to_report')}
          </Button>
        ) : null}

        {loading && !detailData ? (
          <CuratorReportLoading
            titleKey="school_curator_student_page.loading.title"
            subtitleKey="school_curator_student_page.loading.subtitle"
          />
        ) : missingCourse ? (
          <EmptyState
            className={styles.emptyState}
            icon="drafts"
            title={getTranslation(
              'school_curator_student_page.missing_course.title',
            )}
            text={getTranslation(
              'school_curator_student_page.missing_course.text',
            )}
          />
        ) : detailData ? (
          <>
            <div className={styles.header}>
              <div className={styles.headerMain}>
                <Avatar
                  size="lg"
                  url={detailData.student.avatar}
                  text={getStudentAvatarText(detailData.student)}
                />
                <div className={styles.headerInfo}>
                  <Typography
                    className={cn(styles.studentName, {
                      [styles.studentNameUnset]: !hasStudentDetailName(
                        detailData.student,
                      ),
                    })}
                    variant="subtitle"
                    weight="medium"
                    mode="compact"
                  >
                    {getStudentDisplayName(detailData.student)}
                  </Typography>
                  <Typography
                    className={styles.studentEmail}
                    variant="body3"
                    mode="compact"
                  >
                    {detailData.student.email}
                  </Typography>
                </div>
              </div>
              <div className={styles.headerMeta}>
                <div>
                  <Typography
                    className={styles.headerMetaLabel}
                    variant="caption"
                    mode="compact"
                  >
                    {getTranslation(
                      'school_curator_student_page.header.registered_at',
                    )}
                  </Typography>
                  <Typography
                    className={styles.headerMetaValue}
                    variant="body3"
                    mode="compact"
                  >
                    {formatStudentRegisteredAt(
                      detailData.student.registeredAt,
                      locale,
                    )}
                  </Typography>
                </div>
                <div>
                  <Typography
                    className={styles.headerMetaLabel}
                    variant="caption"
                    mode="compact"
                  >
                    {getTranslation(
                      'school_curator_student_page.header.promocode_code',
                    )}
                  </Typography>
                  <div className={styles.headerMetaValueRow}>
                    <Typography
                      className={styles.headerMetaValue}
                      variant="body3"
                      mode="compact"
                    >
                      {formatPromocodeCode(detailData.student.promocodeAccess)}
                    </Typography>
                    {detailData.student.promocodeAccess?.code ? (
                      <Icon
                        className={styles.copyIcon}
                        name="copy"
                        onClick={onCopyPromocodeClick}
                      />
                    ) : null}
                  </div>
                </div>
                <div>
                  <Typography
                    className={styles.headerMetaLabel}
                    variant="caption"
                    mode="compact"
                  >
                    {getTranslation(
                      'school_curator_student_page.header.promocode_validity',
                    )}
                  </Typography>
                  <Typography
                    className={styles.headerMetaValue}
                    variant="body3"
                    mode="compact"
                  >
                    {formatPromocodeAccessRange(
                      detailData.student.promocodeAccess,
                      locale,
                    )}
                  </Typography>
                </div>
              </div>
            </div>

            {detailData.overview.courses.length > 0 ? (
              <div className={styles.overviewSection}>
                <Typography
                  className={styles.sectionTitle}
                  variant="body1"
                  weight="medium"
                  mode="compact"
                >
                  {getTranslation('school_curator_student_page.overview_title')}
                </Typography>

                <div className={styles.overviewCoursesGrid}>
                  {detailData.overview.courses.map((course, index) => (
                    <CourseProgressDonutCard
                      key={course.id}
                      course={course}
                      courseColor={
                        OVERVIEW_COURSES_PROGRESS_COLORS[
                          index % OVERVIEW_COURSES_PROGRESS_COLORS.length
                        ]
                      }
                      buildCourseLink={buildCourseLink}
                      locale={locale}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            <div className={styles.metricsGrid}>
              {getMetricCards(detailData).map((metric) => (
                <div key={metric.key} className={styles.metricCard}>
                  <Typography
                    className={styles.metricLabel}
                    variant="caption"
                    mode="compact"
                  >
                    {metric.label}
                  </Typography>
                  {metric.type === 'status' ? (
                    <div
                      className={cn(
                        styles.badge,
                        styles[getStatusBadgeClassName(metric.value)],
                      )}
                    >
                      <Typography
                        className={styles.badgeText}
                        variant="caption"
                        weight="medium"
                        mode="compact"
                      >
                        {getStudentStatusLabel(metric.value)}
                      </Typography>
                    </div>
                  ) : null}
                  {metric.type === 'risk' ? (
                    !isRiskApplicable(
                      detailData.summary.status,
                      metric.value.level,
                    ) ? (
                      <div
                        className={cn(
                          styles.badge,
                          styles[getRiskNotApplicableBadgeClassName()],
                        )}
                      >
                        <Typography
                          className={styles.badgeText}
                          variant="caption"
                          weight="medium"
                          mode="compact"
                        >
                          {getRiskNotApplicableLabel(
                            detailData.summary.status,
                            metric.value.level,
                          )}
                        </Typography>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          styles.badge,
                          styles[getRiskBadgeClassName(metric.value.level)],
                        )}
                      >
                        <Typography
                          className={styles.badgeText}
                          variant="caption"
                          weight="medium"
                          mode="compact"
                        >
                          {getRiskLevelLabel(metric.value.level)}
                        </Typography>
                      </div>
                    )
                  ) : null}
                  {metric.type === 'text' ? (
                    <Typography
                      className={styles.metricValue}
                      variant="body1"
                      weight="medium"
                      mode="compact"
                    >
                      {metric.value}
                    </Typography>
                  ) : null}
                </div>
              ))}
            </div>

            <ProgressBreakdownSection detailData={detailData} />
          </>
        ) : (
          <EmptyState
            className={styles.emptyState}
            icon="drafts"
            title={getTranslation('school_curator_student_page.not_found.title')}
            text={getTranslation('school_curator_student_page.not_found.text')}
          />
        )}
      </div>
    </div>
  );

  if (isPublic) {
    return pageContent;
  }

  return <MenuLayout>{pageContent}</MenuLayout>;
};

export default React.memo(SchoolCuratorStudentPage) as any;
